import { Head } from "@inertiajs/react";
import { Box, Button, Flex, Section, Text, Theme } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef, useState } from "react";
import { PanelRightClose, X } from "lucide-react";
import {
  TrackManagerCredentialsIndexCredential as Credential,
  TrackManagerCredentialsIndexProps,
} from "@/types/track_manager_credentials_index";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import CredentialsTable from "@/shared/tables/track_manager/CredentialsTable";
import CredentialsCards from "@/shared/track_manager/CredentialsCards";
import CredentialReviewForm from "@/shared/track_manager/CredentialReviewForm";
import useIsMobile from "@/shared/hooks/useIsMobile";
import { Row } from "@tanstack/react-table";

export default function TrackManagerCredentialsIndex({
  credentials,
  focus_credential_id,
}: TrackManagerCredentialsIndexProps) {
  const isMobile = useIsMobile();

  return (
    <div className="flex">
      <Head title="Credentials" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Credentials" />
        <Section size="1">
          <Box px={{ initial: "3", sm: "0" }}>
            {isMobile ? (
              <CredentialsCards
                credentials={credentials}
                focusCredentialId={focus_credential_id}
              />
            ) : (
              <DesktopQueue
                credentials={credentials}
                focusCredentialId={focus_credential_id}
              />
            )}
          </Box>
        </Section>
      </div>
    </div>
  );
}

/** Table plus the slide-in review drawer. Desktop only — see CredentialsCards. */
function DesktopQueue({
  credentials,
  focusCredentialId,
}: {
  credentials: Credential[];
  focusCredentialId: number | null;
}) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [filteredRows, setFilteredRows] = useState<Row<Credential>[] | null>();

  // Arriving from an application under review: open that credential's drawer.
  // Once only — re-opening it on every filter change would fight the manager.
  const focusHandled = useRef(false);
  useEffect(() => {
    if (focusHandled.current || !focusCredentialId || !filteredRows) return;
    focusHandled.current = true;

    const index = filteredRows.findIndex((row) => row.original.id === focusCredentialId);
    if (index >= 0) setSelectedRowIndex(index);
  }, [filteredRows, focusCredentialId]);

  if (credentials.length === 0) {
    return <Text color="gray">No credentials yet.</Text>;
  }

  return (
    <Dialog.Root
      open={selectedRowIndex !== null}
      onOpenChange={(open) => {
        if (!open) setSelectedRowIndex(null);
      }}
    >
      <Box style={{ overflowX: "auto" }}>
        <CredentialsTable
          credentials={credentials}
          setSelectedRowIndex={setSelectedRowIndex}
          setFilteredRows={setFilteredRows}
        />
      </Box>

      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            style={{ position: "fixed", inset: 0, background: "var(--color-overlay)" }}
          />
          <Dialog.Content
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "min(480px, 100vw)",
              height: "100vh",
              backgroundColor: `var(--color-panel-solid)`,
              padding: "var(--space-5)",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <Box p="4" style={{ backgroundColor: "var(--color-panel-solid)" }}>
              <Flex justify="between" align="center" mb="4">
                <Dialog.Title asChild>
                  <Text size="5" weight="bold">
                    Credential Detail
                  </Text>
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="1">
                    <X size="16px" />
                  </Button>
                </Dialog.Close>
              </Flex>
              {selectedRowIndex !== null && filteredRows?.[selectedRowIndex] && (
                <CredentialReviewForm
                  credential={filteredRows[selectedRowIndex].original}
                />
              )}
              <Flex justify="between" align="center" pt="2">
                <Button
                  variant="soft"
                  disabled={selectedRowIndex === null || selectedRowIndex <= 0}
                  onClick={() => setSelectedRowIndex((i) => (i !== null ? i - 1 : null))}
                >
                  Previous
                </Button>
                <Text size="1" color="gray">
                  {selectedRowIndex !== null
                    ? `${selectedRowIndex + 1} / ${filteredRows?.length}`
                    : ""}
                </Text>
                <Button
                  variant="soft"
                  disabled={
                    selectedRowIndex === null ||
                    selectedRowIndex >= (filteredRows?.length ?? 1) - 1
                  }
                  onClick={() => setSelectedRowIndex((i) => (i !== null ? i + 1 : null))}
                >
                  Next
                </Button>
              </Flex>
            </Box>
            <Dialog.Close asChild>
              <Button
                variant="soft"
                size="3"
                className="sm:hidden"
                style={{
                  position: "sticky",
                  bottom: 0,
                  alignSelf: "flex-end",
                  marginTop: "auto",
                  backgroundColor: "var(--color-panel-solid)",
                }}
              >
                <PanelRightClose size="16px" />
                Close
              </Button>
            </Dialog.Close>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
